import { Anchor, Truck, Route } from "lucide-react";
import { Reveal, SectionHead, PageHero, SpecTable, CTASection, usePageMeta } from "../components/ui-bits";
import { IMG, imgUrl, bays, tarapurFunctions, scaleMetrics } from "../data/content";
import { useCmsList, toSpecRows } from "../hooks/useCmsContent";

const LOG_ICONS = { Truck, Anchor, Route };

const GALLERY_FALLBACK = [
  { img: "hmel-loading", alt: "Oversized column sections loaded for dispatch" },
  { img: "mounded-vessels", alt: "Mounded storage vessels at site" },
  { img: "air-ring-welding", alt: "G.R. Engineering Air Ring Fabrication and Welding" },
  { img: "rr-erection-fleet", alt: "G.R. Engineering Heavy Equipment Erection" },
  { img: "rr-tss-structure", alt: "G.R. Engineering Reactor Regenerator and TSS Structure Erection" },
  { img: "jetty-loadout", alt: "G.R. Engineering oversized equipment load-out at captive jetty" },
  { img: "odc-transport-gr", alt: "G.R. Engineering super-heavy column on multi-axle ODC transport" },
  { img: "superheavy-column-site", alt: "G.R. Engineering site-fabricated super-heavy column, HMEL Bathinda" },
  { img: "regenerator-transport", alt: "G.R. Engineering Regenerator Transportation to Site" },
];

const LOGISTICS_FALLBACK = [
  { title: "Road Movement", icon: "Truck", spec_rows: [["Max Weight", "250 MT"], ["Max OD", "6,000 mm"], ["Max Length", "48,000 mm"]], desc: "Direct access to the Western Express Highway; dimensions limited by route constraints such as flyovers, sign gantries and high-tension lines." },
  { title: "Own Jetty — 5 km", icon: "Anchor", spec_rows: [["Distance from Works", "5 km"], ["Mode", "Barge load-out"]], desc: "Oversized equipment rolls straight from the works to our captive jetty for waterway dispatch — no road ceiling on diameter or weight." },
  { title: "Multi-Modal / RO-RO", icon: "Route", spec_rows: [["Waterfront Land", "West Bengal"], ["Operation", "Direct RO-RO"]], desc: "Waterfront fabrication land in West Bengal enabled direct roll-on/roll-off delivery of mounded bullet sections from Kolkata to Mongla, Bangladesh." },
];

const Facilities = () => {
  usePageMeta(
    "Facilities — Tarapur Manufacturing Works & Captive Jetty | G. R. Engineering",
    "Tarapur works with three fabrication bays (up to 27 m × 160 m), bogie hearth furnace 6×6×12 m, direct Western Express Highway access, ODC movement up to 250 MT by road and a captive jetty 5 km away."
  );
  const bayList = useCmsList("facility_bay", bays);
  const gallery = useCmsList("facility_gallery", GALLERY_FALLBACK);
  const logistics = useCmsList("facility_logistics", LOGISTICS_FALLBACK);
  const rawGroups = useCmsList("scale_group", scaleMetrics);
  const groups = rawGroups.map((g) => ({ group: g.group, items: toSpecRows(g.items) }));
  return (
    <>
      <PageHero
        img={IMG("jetty-barge")}
        eyebrow="Facilities"
        title="Tarapur Works — built for the super-heavy"
        sub="A manufacturing complex engineered around one problem: fabricating equipment too large for anyone else to move."
      />

      <section className="bg-white border-b border-slate-200" data-testid="facilities-bays">
        <div className="container-gre py-20 lg:py-28">
          <SectionHead eyebrow="Manufacturing" title="Three dedicated fabrication bays" sub="Light fabrication, machining and heavy fabrication run as parallel production lines — so a 110 m column never queues behind a tube bundle." />
          <div className="mt-14 grid sm:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {bayList.map((b, i) => (
              <Reveal key={b.bay} delay={i * 0.08}>
                <div className="bg-white p-9 h-full card-lift border border-transparent" data-testid={`facility-${b.bay.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5221F]">Bay {b.bay[4]}</p>
                  <p className="mt-4 font-display text-xl font-bold text-slate-900">{b.bay.split("— ")[1]}</p>
                  <p className="mt-3 font-display text-3xl font-black tracking-tight text-slate-900">{b.size}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {tarapurFunctions.map((f) => (
                <span key={f} className="font-mono-tech text-[10px] font-semibold uppercase tracking-widest border border-slate-200 bg-slate-50 text-slate-600 px-3.5 py-2">{f}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 border-b border-slate-200" data-testid="facilities-scale">
        <div className="container-gre py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHead eyebrow="Engineering Scale" title="Verified maximum parameters" sub="What the works can fabricate as a single piece — and what site fabrication adds beyond it." />
            <div className="mt-10 space-y-6">
              {groups.map((g) => (
                <div key={g.group} className="border border-slate-200 bg-white">
                  <p className="px-5 py-3 bg-[#0A1128] font-mono-tech text-[10px] font-bold uppercase tracking-widest text-white">{g.group}</p>
                  <SpecTable rows={g.items} />
                </div>
              ))}
              <SpecTable rows={[
                ["Bogie Hearth Furnace", "6 m H × 6 m W × 12 m L"],
                ["Hydrostatic Testing", "Up to 500 kg/cm² g"],
                ["Impact Testing", "Down to −195 °C (NABL lab)"],
              ]} />
            </div>
          </div>
          <div className="space-y-6 lg:sticky lg:top-28" data-testid="facilities-gallery">
            {gallery.map((g, i) => (
              <Reveal key={g.id || g.img} delay={Math.min(i * 0.05, 0.3)}>
                <img src={imgUrl(g.img)} alt={g.alt} loading="lazy" data-testid={`facilities-img-${i}`} className="w-full aspect-[16/8] object-cover img-industrial border border-slate-200" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A1128] relative overflow-hidden" data-testid="facilities-logistics">
        <div className="absolute inset-0">
          <img src={IMG("jetty-barge")} alt="" className="w-full h-full object-cover img-industrial opacity-15" />
          <div className="absolute inset-0 blueprint-grid" />
        </div>
        <div className="container-gre relative py-20 lg:py-28">
          <SectionHead light eyebrow="Transportation & Logistics" title="By road up to 250 MT. By water, beyond all limits." sub="The works has direct access to the Western Express Highway. For equipment that cannot move by road, our own jetty lies just 5 km from the manufacturing complex." />
          <div className="mt-14 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {logistics.map((l, i) => {
              const Icon = LOG_ICONS[l.icon] || Truck;
              return (
              <Reveal key={l.id || l.title} delay={i * 0.08}>
                <div className="bg-[#0A1128]/70 backdrop-blur-sm p-8 h-full flex flex-col" data-testid={`logistics-card-${i}`}>
                  <Icon size={26} className="text-[#E8574F]" strokeWidth={1.6} />
                  <p className="mt-5 font-display text-lg font-bold text-white">{l.title}</p>
                  <p className="mt-3 text-[13px] text-slate-400 leading-relaxed flex-1">{l.desc}</p>
                  <div className="mt-6"><SpecTable rows={l.spec_rows} light /></div>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default Facilities;
